import { DialogTitle } from '@headlessui/react'
import { TrashIcon } from '@heroicons/react/24/outline'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useNotificationContext } from '../../../context'
import { mutation, query } from '../../../graphql'
import { ROLES } from '../../../helpers/authorization-roles'
import Input from '../../input'
import { useLockTexts } from '../../pricing/lock-badge'
import UpgradeInline from '../../pricing/upgrade-inline'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'
import SelectMenuWithDescription from '../../tailwind/select-menu-with-description'
import ModalDeleteUser from './modal-delete-user'
import {
  FREE_ROLE_ID,
  PERMISSION_FEATURE,
  lockedRoleIds,
  resolveSelectedRole
} from './role-lock'
import TagsEditor, { flushTagInput } from './tags-editor'

const PERMISSION_SOURCE = 'settings_user_edit'

const sameTags = (a, b) =>
  a.length === b.length && a.every((tag) => b.includes(tag))

const ModalEditUser = ({ open, setOpen, data, suggestions, onSuccess }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()

  const [isUpdating, setIsUpdating] = useState(false)
  const [askForDeleting, setAskForDeleting] = useState(false)

  const { space_id, email, firstname, lastname, role_id, is_current_user } =
    data
  const initialTags = data.tags || []

  const roles = Object.values(ROLES).map(({ id, title, description }) => ({
    id,
    title: t(title),
    description: t(description)
  }))

  const [selectedRole, setSelectedRole] = useState(
    roles.find(({ id }) => id === role_id) || roles[1]
  )
  const [tags, setTags] = useState(initialTags)
  const [tagInput, setTagInput] = useState('')
  const [showPermissionUpgrade, setShowPermissionUpgrade] = useState(false)

  // Ohne Berechtigungssystem (nur enforced) bleibt die bestehende Rolle
  // stehen, ein Wechsel zu Admin geht immer, alle anderen Wechsel sind
  // gesperrt
  const {
    gate: permissionGate,
    label: lockedRoleLabel,
    ariaLabel: lockedRoleAriaLabel,
    includedText: lockedRoleTitle
  } = useLockTexts(PERMISSION_FEATURE, { source: PERMISSION_SOURCE })
  const lockedRoles = lockedRoleIds({
    locked: permissionGate.locked,
    currentRoleId: role_id
  })
  const role = resolveSelectedRole({
    roles,
    selected: selectedRole,
    lockedIds: lockedRoles,
    fallbackId: role_id
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstname: firstname || '',
      lastname: lastname || ''
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required(
        t('settings.user_management.input_firstname_validation')
      ),
      lastname: Yup.string().required(
        t('settings.user_management.input_lastname_validation')
      )
    })
  })

  const handleCancel = () => {
    setOpen(false)
    setAskForDeleting(false)
    setTimeout(() => {
      setSelectedRole(roles[1])
      setTags(initialTags)
      setTagInput('')
      setShowPermissionUpgrade(false)
      formik.resetForm()
    }, 1000 * 0.4)
  }

  const effectiveTags = flushTagInput(tags, tagInput)
  const role_changed = !is_current_user && role?.id !== role_id
  const tags_changed = !sameTags(effectiveTags, initialTags)

  const updateUser = async () => {
    setIsUpdating(true)

    try {
      const requests = []

      if (role_changed) {
        requests.push(
          query({
            query: 'spaceControl',
            variables: {
              action: 'setAuthorizations',
              space_id,
              authorization_role: role.id
            }
          })
        )
      }

      if (tags_changed) {
        requests.push(
          mutation({
            mutation: 'updateSpace',
            input: { id: space_id, tags: effectiveTags }
          })
        )
      }

      await Promise.all(requests)

      success(t('settings.user_management.update_user_success'))
      onSuccess()
      setOpen(false)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <>
      <Modal open={open} setOpen={handleCancel} size='lg'>
        <div className='mt-4 text-center'>
          <DialogTitle
            as='h3'
            className='text-lg font-medium leading-6 text-gray-900'
          >
            {t('settings.user_management.dialog_title_update')}
          </DialogTitle>
        </div>
        <div className='mt-6'>
          {[
            [
              'firstname',
              t('settings.user_management.input_given_name_label'),
              t('settings.user_management.input_given_name_placeholder')
            ],
            [
              'lastname',
              t('settings.user_management.input_family_name_label'),
              t('settings.user_management.input_family_name_placeholder')
            ]
          ].map((input, index) => (
            <div key={index} className='mt-4'>
              <Input
                id={input[0]}
                label={input[1]}
                placeholder={input[2]}
                type='text'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                touched={formik.touched[input[0]]}
                value={formik.values[input[0]]}
                error={formik.errors[input[0]]}
                disabled
              />
            </div>
          ))}
          <div className='mt-4'>
            <Input
              id='email'
              label={t('settings.user_management.disabled_input_email_label')}
              type='text'
              value={email}
              disabled
            />
          </div>
          <div className='mt-4'>
            <SelectMenuWithDescription
              id='role'
              label={t('settings.user_management.manage_rights_label')}
              selected={role}
              setSelected={setSelectedRole}
              options={roles}
              disabled={is_current_user}
              lockedOptionIds={lockedRoles}
              onLockedSelect={() => setShowPermissionUpgrade(true)}
              lockedLabel={lockedRoleLabel}
              lockedAriaLabel={lockedRoleAriaLabel}
              lockedTitle={lockedRoleTitle}
            />
            {is_current_user && (
              <p className='mt-1.5 text-xs italic text-gray-400'>
                {t('settings.user_management.edit_self_hint')}
              </p>
            )}
            {permissionGate.locked && !is_current_user && (
              <>
                <p className='mt-1.5 text-xs text-gray-500'>
                  {t(
                    role_id === FREE_ROLE_ID
                      ? 'pricing.permission.role_locked_hint'
                      : 'pricing.permission.existing_role_hint'
                  )}
                </p>
                {/* Im Modal UpgradeInline statt des Upgrade-Dialogs, HeadlessUI
                    stapelt Geschwister-Dialoge nicht sauber */}
                <div aria-live='polite'>
                  {showPermissionUpgrade && (
                    <UpgradeInline
                      feature={PERMISSION_FEATURE}
                      source={PERMISSION_SOURCE}
                      compact
                      className='mt-3'
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div className='mt-4'>
            <TagsEditor
              tags={tags}
              setTags={setTags}
              suggestions={suggestions}
              input={tagInput}
              setInput={setTagInput}
            />
          </div>
        </div>
        {!is_current_user && (
          <div className='mt-8 flex justify-end'>
            <div
              className='group flex cursor-pointer items-center'
              onClick={() => {
                setOpen(false)
                setAskForDeleting(true)
              }}
            >
              <div className='pt-0.5'>
                <Button.WarningText
                  text={t('settings.user_management.dialog_delete_action')}
                />
              </div>
              <TrashIcon className='ml-1.5 h-6 w-6 text-red-500 group-hover:text-red-600' />
            </div>
          </div>
        )}
        <div className='mt-12 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
          <Button.SecondaryLG
            text={t('settings.user_management.dialog_cancel_action')}
            onClick={handleCancel}
            disabled={isUpdating}
          />
          <Button.PrimaryLG
            text={t('settings.user_management.dialog_update_action')}
            onClick={updateUser}
            isLoading={isUpdating}
            disabled={!role_changed && !tags_changed}
          />
        </div>
      </Modal>
      <ModalDeleteUser
        open={askForDeleting}
        onClose={() => {
          setAskForDeleting(false)
          setOpen(true)
        }}
        onSuccess={() => {
          setAskForDeleting(false)
          onSuccess()
        }}
        data={data}
      />
    </>
  )
}

ModalEditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  suggestions: PropTypes.array,
  onSuccess: PropTypes.func.isRequired
}

ModalEditUser.defaultProps = {
  suggestions: []
}

export default ModalEditUser
